import React, {useEffect} from "react";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Tag} from "primereact/tag";
import {useContentTypeData} from "../../../system/use-content-type-data";
import {useTranslate} from "../../../system/useTranslate";
import MarkdownHelp from "../../../system/components/markdown-help";
import {DataState, RTL_DATATABLE_PROPS, SalesSectionHeader, TableCard} from "./common";
import {entityLabel, formatDate, formatMoney, statusSeverity} from "../sales-utils";

export function statusColumn(field, headerKey = "sales-status") {
    return {
        field,
        headerKey,
        body: (row, translate) => <Tag className="text-xs" severity={statusSeverity(row[field])} value={row[field] || translate("sales-status-unknown")}/>,
    };
}

export function dateColumn(field, headerKey = "sales-date") {
    return {field, headerKey, body: (row) => formatDate(row[field])};
}

export function moneyColumn(field, headerKey = "sales-amount") {
    return {field, headerKey, body: (row, translate) => formatMoney(row[field], row.currency, translate)};
}

export function relationColumn(field, headerKey) {
    return {field, headerKey, body: (row) => entityLabel(row[field])};
}

export default function BillingCollectionTable({
    uid,
    titleKey,
    descriptionKey,
    columns,
    actions,
    rowAction,
    sort = "createdAt:DESC",
    pageSize = 10,
}) {
    const translate = useTranslate();
    const collection = useContentTypeData(uid);

    useEffect(() => {
        collection.ensure({sort}, {all: true}).catch(() => undefined);
        // The hook methods are stable for a UID; reloading is explicit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, uid]);

    return (
        <div className="flex flex-column gap-3">
            <SalesSectionHeader
                title={translate(titleKey)}
                description={descriptionKey ? translate(descriptionKey) : null}
                actions={<>
                    {actions}
                <Button className="text-xs gap-2" icon="pi pi-refresh" label={translate("refresh")} outlined loading={collection.loading} onClick={() => collection.reload({sort}, {all: true})}/>
                </>}
            />
            <TableCard>
                <DataState loading={collection.loading} error={collection.error} empty={!collection.items.length}>
                    <DataTable {...RTL_DATATABLE_PROPS} value={collection.items} paginator rows={pageSize}>
                        {columns.map((column) => (
                            <Column
                                key={column.field}
                                field={column.field}
                                header={translate(column.headerKey)}
                                body={column.body ? (row) => column.body(row, translate) : undefined}
                                className="text-right"
                            />
                        ))}
                        {rowAction && (
                            <Column className="text-right" header={translate("actions")}
                                    body={(row) => rowAction(row, () => collection.reload({sort}, {all: true}))}/>
                        )}
                    </DataTable>
                </DataState>
            </TableCard>
            <MarkdownHelp contentKey={`${titleKey}-help`}/>
        </div>
    );
}
